import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton } from '@mylighthouse/prism-ember';
import HotelPricingAssistantTableCellsTdOwnRateDynamicRatePricingCellValue from 'lumen/components/hotel/pricing-assistant/table/cells/td-own-rate-dynamic-rate-pricing-cell-value';
import HotelPricingAssistantTablePopoversOverrideCellPopover from 'lumen/components/hotel/pricing-assistant/table/popovers/override-cell-popover';
import type {
  TablePricingManagerCustomColumn,
  TablePricingManagerCustomRow,
} from 'lumen/components/hotel/pricing-assistant/table/table-pricing-manager';
import TooltipOwnRateCell from 'lumen/components/hotel/pricing-assistant/table/tooltips/tooltip-own-rate-cell';
import type { Carate, Carecommendation } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';

import styles from './td-own-rate-dynamic-rate-pricing.module.css';

interface HotelPricingAssistantTableCellsTdOwnRateDynamicRatePricingSignature {
  Args: {
    column: TablePricingManagerCustomColumn;
    row: TablePricingManagerCustomRow;
    value: Carate;
  };
}

export default class HotelPricingAssistantTableCellsTdOwnRateDynamicRatePricing extends Component<HotelPricingAssistantTableCellsTdOwnRateDynamicRatePricingSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service
  declare pricingAssistantRecommendationActionPoller: Services['pricing-assistant-recommendation-action-poller'];

  @tracked isPopoverOpen = false;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get carate() {
    return this.args.value;
  }

  get carecommendation(): Carecommendation | null {
    return this.carate.carecommendation;
  }

  get displayFormattedRate(): string | null {
    return !this.isPast
      ? this.carate.formattedDynamicRatePricingPriceToShow
      : this.carate.formattedRate;
  }

  get hasOverridePopover(): boolean {
    if (
      !this.access.hasPricingAssistantEditRights ||
      !this.access.hasPricingAssistantDynamicRatePricingFeature
    ) {
      return false;
    }

    return !this.isPast;
  }

  get isLoading(): boolean {
    return (
      this.showRecommendationBeingPushedOrIsPushed ||
      this.args.column.isColumnUpdating
    );
  }

  get isPast(): boolean {
    return this.hotelAndMarketService.isPast(this.args.column.date);
  }

  get isTooltipVisible(): boolean {
    return !this.isPopoverOpen;
  }

  get popoverActionVisibility(): {
    delete: boolean;
    edit: boolean;
  } {
    const showOverride = this.carate.showOverride;

    return {
      delete: showOverride,
      edit: true,
    };
  }

  get priceChangeDirection(): 'up' | 'down' | null {
    if (this.isPast || !this.carecommendation) {
      return null;
    }
    const diff =
      this.carecommendation.formattedDifferenceBetweenRecommendationAndOwnRate;
    if (!diff || diff.difference === 0) {
      return null;
    }
    return diff.isPositiveDifference ? 'up' : 'down';
  }

  get showException(): boolean {
    return !this.isPast && Boolean(this.carecommendation?.hasException);
  }

  get showOverride(): boolean {
    return this.carate.showOverride;
  }

  get showRecommendation(): boolean {
    return !this.isPast && Boolean(this.carecommendation);
  }

  get showRecommendationBeingPushedOrIsPushed(): boolean {
    const date = this.args.column.date.format('YYYY-MM-DD');
    return this.pricingAssistantRecommendationActionPoller.recommendationDatesAffectedByOverridePush.has(
      date,
    );
  }

  @action
  onClick(): void {
    if (!this.hasOverridePopover) {
      return;
    }

    this.isPopoverOpen = true;
  }

  @action
  onDoubleClick(): void {
    if (!this.access.isStaffUser && !console) {
      return;
    }

    const dateStr = this.args.column.date.format('YYYY-MM-DD');
    const hotelName = this.carate?.hotelinfo.name;
    const { roomName } = this.args.row.room;
    const maybeTableLog = console?.table ?? console.log;

    console.group(
      `Debug: Pricing Manager Recommendation Cell - ${hotelName} - ${roomName} - ${dateStr}`,
    );

    if (this.carate) {
      console.log('Carate object:');
      maybeTableLog(this.carate.toJSON());
    } else {
      console.log('carate is undefined');
    }

    if (this.carecommendation) {
      console.log('Carecommendation object:');
      maybeTableLog(this.carecommendation.toJSON());
    } else {
      console.log('No Carecommendation for this Carate.');
    }

    console.groupEnd();
  }

  @action
  async onOverridePopoverDeleteButtonClick(): Promise<void> {
    await this.args.row.deleteOverride(this.carate);
  }

  @action
  onOverridePopoverEditButtonClick(): void {
    this.args.row.openEditOverrideModal(this.carate);
  }

  <template>
    {{! template-lint-disable no-invalid-interactive }}
    {{#if this.isLoading}}
      <div class="loading-pulse" data-test-id="loading-pulse"></div>
    {{else}}
      <div
        class="text-300--regular truncate tabular-nums
          {{styles.price-cell}}
          {{if this.isPast 'text-disabled'}}
          {{if this.hasOverridePopover styles.has-popover}}
          {{if this.showException styles.has-exception}}"
        data-test-id="td-own-rate-dynamic-rate-pricing-cell"
        {{on "click" this.onClick}}
        {{on "dblclick" this.onDoubleClick}}
      >
        {{this.displayFormattedRate}}
        <HotelPricingAssistantTableCellsTdOwnRateDynamicRatePricingCellValue
          @isPast={{this.isPast}}
          @priceChangeDirection={{this.priceChangeDirection}}
          @showException={{this.showException}}
          @showOverride={{this.showOverride}}
        />
        {{#if this.hasOverridePopover}}
          <HotelPricingAssistantTablePopoversOverrideCellPopover
            @dataTestId="exact-price-override-popover"
            @isShown={{this.isPopoverOpen}}
          >
            <div class="{{styles.price-overlay}} tabular-nums">
              {{this.displayFormattedRate}}
              <HotelPricingAssistantTableCellsTdOwnRateDynamicRatePricingCellValue
                @isPast={{this.isPast}}
                @priceChangeDirection={{this.priceChangeDirection}}
                @showException={{this.showException}}
                @showOverride={{this.showOverride}}
              />
            </div>
            <div class={{styles.button-container}}>
              {{#if this.popoverActionVisibility.delete}}
                <PrismButton
                  @buttonType="secondary"
                  @icon="bin"
                  @onClick={{this.onOverridePopoverDeleteButtonClick}}
                  @size="small"
                  class="w-full"
                  data-test-id="delete-exact-price-override-button"
                />
              {{/if}}
              {{#if this.popoverActionVisibility.edit}}
                <PrismButton
                  @buttonType="primary"
                  @icon="edit-pencil"
                  @onClick={{this.onOverridePopoverEditButtonClick}}
                  @size="small"
                  class="w-full"
                  data-test-id="edit-exact-price-override-button"
                />
              {{/if}}
            </div>
          </HotelPricingAssistantTablePopoversOverrideCellPopover>
        {{/if}}
      </div>
    {{/if}}
    {{#if this.isTooltipVisible}}
      <TooltipOwnRateCell
        @carate={{this.carate}}
        @column={{@column}}
        @isPriceLoading={{this.isLoading}}
      />
    {{/if}}
  </template>
}
